import { type FC, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { FlowController } from "@/shared/components/FlowController";
import { getModuleById } from "@/shared/constants/estateModules";
import { filterByCategory } from "@/shared/constants/estateCategories";
import { useEstateTasks } from "@/shared/hooks/useEstateTasks";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useDocuments } from "@/shared/hooks/useDocumentStorage";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";

import { getScreensForModule, getResumeStep } from "./taskScreenRegistry";
import type { FlowSection, FlowAnswers } from "@/shared/constants/flowTypes";

interface ModuleLocationState {
  returnTo?: string;
}

export const ModulePage: FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo =
    (location.state as ModuleLocationState | null)?.returnTo ?? POST_LOSS_ROUTES.OVERVIEW;

  const { person } = useAppContext();
  const firstName = person?.first_name ?? "their";
  const fullName = person?.full_name ?? firstName;
  const { tier } = usePaymentTier();
  const isPremium = hasMinTier(tier, "basic");
  const { assets, debts } = useEstateItems(person?.estate_id);
  const { data: documentsData } = useDocuments();
  const { isLoading: isProgressLoading } = useModuleProgress();

  const documentTypeLabels = useMemo(() => {
    const labels: Record<number, string> = {};
    for (const doc of documentsData?.documents ?? []) {
      labels[doc.id] = getDocumentTypeLabel(doc.document_type);
    }
    return labels;
  }, [documentsData]);

  const module = moduleId ? getModuleById(moduleId) : undefined;
  const descriptor = useMemo(
    () =>
      module
        ? getScreensForModule(module, firstName, { assets, debts, documentTypeLabels, fullName })
        : { tasks: [], allScreens: [], screenTaskId: {} },
    [module, firstName, fullName, assets, debts, documentTypeLabels],
  );

  const { isLoading, initialAnswers, completedTaskIds, saveStep, completeModule } = useEstateTasks({
    moduleId: moduleId ?? "",
    tasks: descriptor.tasks,
    screenTaskId: descriptor.screenTaskId,
  });

  useEffect(() => {
    if (moduleId !== "estate-setup" || isProgressLoading) return;
    navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
  }, [moduleId, isProgressLoading, navigate]);

  const handleClose = useCallback(() => {
    navigate(returnTo);
  }, [navigate, returnTo]);

  const handleComplete = useCallback(() => {
    completeModule();
    navigate(returnTo);
  }, [completeModule, navigate, returnTo]);

  const section = useMemo((): FlowSection | null => {
    if (!module || descriptor.allScreens.length === 0) return null;

    return {
      id: module.id,
      title: module.title,
      screens: descriptor.allScreens,
      allowSaveAndClose: true,
      saveOnEachStep: true,
      onComplete: handleComplete,
      onStepComplete: saveStep,
    };
  }, [module, descriptor.allScreens, handleComplete, saveStep]);

  const propertiesParsedSinceLastVisit =
    moduleId === "real-estate" &&
    filterByCategory(assets, "Properties").length > 0 &&
    initialAnswers.properties_review === "none";

  const mergedAnswers = useMemo(() => {
    const base: FlowAnswers = { ...initialAnswers, ...(isPremium ? { is_premium: "yes" } : {}) };

    if (propertiesParsedSinceLastVisit) {
      delete base.properties_review;
      delete base.has_property;
    }

    return base;
  }, [initialAnswers, isPremium, propertiesParsedSinceLastVisit]);

  const adjustedCompletedTaskIds = useMemo(() => {
    if (!propertiesParsedSinceLastVisit) return completedTaskIds;
    const adjusted = new Set(completedTaskIds);
    adjusted.delete("properties");
    return adjusted;
  }, [completedTaskIds, propertiesParsedSinceLastVisit]);

  const initialStep = useMemo(
    () =>
      getResumeStep(
        descriptor.allScreens,
        mergedAnswers,
        descriptor.screenTaskId,
        adjustedCompletedTaskIds,
      ),
    [descriptor.allScreens, mergedAnswers, descriptor.screenTaskId, adjustedCompletedTaskIds],
  );

  if (!module) {
    navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
    return null;
  }

  if (moduleId === "estate-setup") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!section) {
    navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
    return null;
  }

  return (
    <FlowController
      section={section}
      onClose={handleClose}
      initialStep={initialStep}
      initialAnswers={mergedAnswers}
    />
  );
};
